import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";

import {
  User,
  Mail,
  CreditCard,
  Shield,
  Key,
  Lock,
  Eye,
  EyeOff,
  LogOut,
  Loader2,
  Sparkles,
  Code2,
  LifeBuoy,
  FileText,
  Lightbulb,
} from "lucide-react";
import { getAuth } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import useCoinStore from "../stores/coinStore";
import accountService from "../Services/accountService";
import CoinStore from "../Components/CoinStore";
import SubscriptionModal from "../Components/SubscriptionModal";
import AccountUpgradeModal from "../Components/AccountUpgradeModal";
import authService from "../Services/authService";
import LoginModal from "../Components/LoginModal";
import mobileAuthService from "../Services/mobileAuthService";
import feedbackService from "../Services/feedbackService";
import { UPDATES_SEEN_KEY } from "./Feedback";
import pastUpdates from "../content/pastUpdates";
import SettingsSection from "../Components/Settings/SettingsSection";
import SettingsRow from "../Components/Settings/SettingsRow";
import ProfileHeaderCard from "../Components/Settings/ProfileHeaderCard";
import TransactionHistoryModal from "../Components/Settings/TransactionHistoryModal";
import AccountSheet from "../Components/Settings/AccountSheet";
import { ReceiptEmailSheet, AddPasswordSheet, ChangePasswordSheet } from "../Components/Settings/AccountEditSheets";
import ProSheet from "../Components/Settings/ProSheet";
import ContactSheet from "../Components/Settings/ContactSheet";
import EditNameModal from "../Components/EditNameModal";
import useConfirm from "../Components/Modals/useConfirm";
import useProfileStore from "../stores/profileStore";

const Account = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [confirmDialog, ask] = useConfirm();
  const auth = getAuth();
  const { displayName, loadDisplayName, clearDisplayName } = useProfileStore();
  const [showEditName, setShowEditName] = useState(false);
  const [savedEmail, setSavedEmail] = useState("");
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCoinStore, setShowCoinStore] = useState(false);
  const [coinStorePreset, setCoinStorePreset] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [billingHistory, setBillingHistory] = useState([]);
  const [spendingHistory, setSpendingHistory] = useState([]);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showLinkEmailModal, setShowLinkEmailModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [accountInfo, setAccountInfo] = useState({
    type: "Guest",
    email: null,
    createdAt: null,
    totalRooms: 0,
  });

  // What's New opens the Updates tab on the feedback board, which marks
  // updates seen. The dot shows while a newer update than that exists.
  const [hasUnreadUpdates, setHasUnreadUpdates] = useState(false);
  useEffect(() => {
    let seen = "";
    try {
      seen = localStorage.getItem(UPDATES_SEEN_KEY) || "";
    } catch {
      /* storage unavailable */
    }
    feedbackService
      .listChangelog()
      .then((entries) => {
        const dates = [
          ...entries.map((e) => (e.date?.toDate ? e.date.toDate() : new Date(e.date))),
          ...pastUpdates.map((u) => new Date(u.date)),
        ].filter((d) => !Number.isNaN(d.getTime()));
        const newest = dates.length ? new Date(Math.max(...dates)).toISOString().slice(0, 10) : "";
        setHasUnreadUpdates(!!newest && newest > seen);
      })
      .catch(() => {});
  }, []);


  const { coinBalance, syncBalance, resetStore, isPro, proExpiry, proType, revokePro } = useCoinStore();
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showAccountSheet, setShowAccountSheet] = useState(false);
  const [showContactSheet, setShowContactSheet] = useState(false);
  const [cancellingPro, setCancellingPro] = useState(false);
  const [showProSheet, setShowProSheet] = useState(false);

  useEffect(() => {
    // If the mobile app opened this page with a custom token in the URL,
    // sign in silently first — then the auth state listener fires normally.
    mobileAuthService.handleTokenFromUrl().catch(console.error);

    // The mobile Coin Store button used to link to /store, a route that never existed
    // in this app's router — this is the real destination now. openStore=true jumps
    // straight to the modal instead of making the player find it themselves.
    const params = new URLSearchParams(window.location.search);
    if (params.get("openPro") === "true") {
      setShowSubscriptionModal(true);
      params.delete("openPro");
      const proSearch = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (proSearch ? `?${proSearch}` : ""));
    }

    if (params.get("openStore") === "true") {
      setShowCoinStore(true);

      // Mobile already picked a package and generated a payment reference before
      // redirecting here — carry it into CoinStore so the picker is pre-selected
      // instead of making the player choose again, and so the reference can ride
      // along in the Paystack metadata.
      const coins = params.get("coins");
      const price = params.get("price");
      const ref = params.get("ref");
      if (coins && price) {
        setCoinStorePreset({
          coins: parseInt(coins, 10),
          price: parseInt(price, 10),
          mobileRef: ref || null,
        });
      }

      params.delete("openStore");
      params.delete("coins");
      params.delete("price");
      params.delete("ref");
      const newSearch = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (newSearch ? `?${newSearch}` : ""));
    }

    // Listen for auth state changes
    // Fires once immediately with the current user (that is the initial load)
    // and again on every sign-in, sign-up, upgrade and sign-out.
    // "Create Account" elsewhere (e.g. My Rooms) lands here with sign-up open.
    if (location.state?.openSignUp) {
      setShowUpgradeModal(true);
      navigate(location.pathname, { replace: true, state: null });
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log("🔄 Auth state changed:", user?.uid, user?.isAnonymous);
      refreshAccountData();
      loadDisplayName({ force: true });
    });

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [auth]); // eslint-disable-line react-hooks/exhaustive-deps

  const detectAccountType = () => {
    const user = auth.currentUser;
    console.log("🔍 Detecting account type for user:", user);

    if (!user) {
      console.log("❌ No user found - truly signed out");
      return { type: "Guest", email: null, isVisitor: true };
    }

    console.log("👤 User details:", {
      isAnonymous: user.isAnonymous,
      email: user.email,
      providerData: user.providerData,
    });

    if (user.isAnonymous) {
      console.log("🕶️ User is anonymous");
      return { type: "Guest", email: null };
    }

    // Check for Google provider
    const googleProvider = user.providerData.find(
      (p) => p.providerId === "google.com"
    );
    if (googleProvider) {
      console.log("🟢 Google account detected");
      return {
        type: "Google Account",
        email: googleProvider.email,
        // The account's own name first: it's what Manage Account edits.
        displayName: user.displayName || googleProvider.displayName,
      };
    }

    // Check for email/password provider
    const emailProvider = user.providerData.find(
      (p) => p.providerId === "password"
    );
    if (emailProvider) {
      console.log("📧 Email account detected");
      return {
        type: "Email Account",
        email: emailProvider.email,
        displayName: user.displayName || null,
      };
    }

    console.log("❓ Unknown account type, defaulting to Unknown");
    return { type: "Unknown", email: user.email };
  };

  const handleLoginSuccess = (result) => {
    console.log("✅ Login successful:", result.message);
    setShowLoginModal(false);
    // Sign-in may still be merging the guest's data; refresh once it is done.
    refreshAccountData();
  };

  const handleLogout = async () => {
    const ok = await ask({
      title: "Sign out?",
      message: "You can sign back in any time to get your coins and rooms.",
      confirmLabel: "Sign out",
    });
    if (ok) {
      setLoading(true);
      try {
        resetStore();
        clearDisplayName();
        await authService.signOut();
      } catch (error) {
        console.error("Logout error:", error);
      } finally {
        window.location.reload();
      }
    }
  };

  // Auth changes, sign-in callbacks and the initial mount can all ask for a
  // reload at nearly the same moment. Run one at a time and, if more were
  // asked for meanwhile, run exactly one more afterwards so the screen always
  // ends on the latest state instead of racing older loads.
  const loadingRef = useRef(false);
  const queuedRef = useRef(false);
  const refreshAccountData = async () => {
    if (loadingRef.current) {
      queuedRef.current = true;
      return;
    }
    loadingRef.current = true;
    try {
      do {
        queuedRef.current = false;
        await loadAccountData();
      } while (queuedRef.current);
    } finally {
      loadingRef.current = false;
    }
  };

  const loadAccountData = async () => {
    setLoading(true);
    setError("");

    try {
      // Check if user exists without creating anonymous
      const userId = authService.getCurrentUserIdIfExists();

      if (!userId) {
        // No user at all - show guest mode and reset coin store
        resetStore();
        setAccountInfo({
          type: "Guest",
          isVisitor: true,
          email: null,
          createdAt: null,
          totalRooms: 0,
        });
        setSavedEmail("");
        setBillingHistory([]);
        setSpendingHistory([]);
        setLoading(false);
        return;
      }

      // User exists - detect account type
      const accountType = detectAccountType();
      console.log("🔍 Account type detected:", accountType);

      // Independent reads, so fetch them together instead of one after another.
      const [, savedPurchaseEmail, userRooms, summary, billing, spending] = await Promise.all([
        syncBalance(),
        accountService.getSavedEmail(),
        authService.getUserRooms(),
        accountService.getAccountSummary(),
        accountService.getBillingHistory(),
        accountService.getSpendingHistory(),
      ]);
      setSavedEmail(savedPurchaseEmail || "");

      setAccountInfo({
        ...accountType,
        createdAt: summary?.createdAt,
        totalRooms: userRooms.length,
      });

      setBillingHistory(billing);
      setSpendingHistory(spending);
    } catch (err) {
      console.error("Failed to load account data:", err);
      setError("Failed to load account information");
    } finally {
      setLoading(false);
    }
  };
  const handleUpgradeSuccess = (result) => {
    console.log("✅ Account upgrade successful:", result.message);
    setShowUpgradeModal(false);
    refreshAccountData();
  };

  const handleCancelSubscription = async () => {
    setCancellingPro(true);
    setError("");
    try {
      const functions = getFunctions();
      const cancelFn = httpsCallable(functions, "cancelProSubscription");
      await cancelFn();
      setSuccessMessage("Cancellation request received. We'll process it within 24 hours — you'll keep Pro until then.");
      return null;
    } catch (err) {
      console.error("Cancel subscription error:", err);
      return "We couldn't send your cancellation request. Please try again.";
    } finally {
      setCancellingPro(false);
    }
  };

  // Visitors and anonymous players are both shown as a guest.
  const isGuestOrAnonymous = accountInfo.type === "Guest";

  let proSubtitle = "";
  if (isPro && proExpiry) {
    const expiry = new Date(proExpiry);
    const daysLeft = Math.ceil((expiry - new Date()) / (1000 * 60 * 60 * 24));
    proSubtitle =
      (proType === "monthly" ? "Next billing " : "Expires ") +
      expiry.toLocaleDateString("en-NG", { dateStyle: "medium" }) +
      (daysLeft > 0 ? ` · ${daysLeft}d left` : "");
  }

  return (
    <>
      <div className="container mx-auto px-4 pt-4 pb-10 max-w-2xl">
        <h1 className="sr-only">Settings</h1>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 text-sm">
            {successMessage}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
          </div>
        ) : (
          <>
            <ProfileHeaderCard
              accountInfo={accountInfo}
              displayName={displayName}
              onEditName={() => setShowEditName(true)}
              coinBalance={coinBalance}
              isPro={isPro}
              proSubtitle={isPro ? proSubtitle : undefined}
              onBuyCoins={() => setShowCoinStore(true)}
              onGoPro={() => setShowSubscriptionModal(true)}
              onManagePro={() => setShowProSheet(true)}
            />

            {/* Guests get one clear "keep your progress" card up top, in place
                of a Sign in pill in the header and a paragraph at the bottom. */}
            {isGuestOrAnonymous && (
              <div className="bg-brand-surface rounded-2xl border border-brand-border shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-4 mb-5">
                <p className="text-sm font-bold text-brand-text">Keep your coins and rooms</p>
                <p className="text-xs text-brand-textSecondary mt-0.5 mb-3">
                  As a guest they only live on this device. Make an account to play anywhere.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setShowUpgradeModal(true)}
                    className="min-h-[44px] rounded-xl bg-brand-accent hover:bg-red-700 text-white text-sm font-semibold transition-colors"
                  >
                    Create account
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowLoginModal(true)}
                    className="min-h-[44px] rounded-xl border border-red-200 text-brand-accent text-sm font-semibold hover:bg-red-50 transition-colors"
                  >
                    Sign in
                  </button>
                </div>
              </div>
            )}

            {/* Account */}
            {!isGuestOrAnonymous && (
              <SettingsSection title="Account">
                <SettingsRow
                  // One name everywhere: the game name, not the Google full name.
                  title={displayName || accountInfo.email || "Your account"}
                  subtitle={[
                    accountInfo.type === "Google Account" ? "Google account" : "Email account",
                    savedEmail ? `receipts to ${savedEmail}` : null,
                  ].filter(Boolean).join(" · ")}
                  icon={User}
                  onClick={() => setShowAccountSheet(true)}
                />
                <SettingsRow
                  title="Transaction history"
                  subtitle="Purchases and coin activity"
                  icon={CreditCard}
                  onClick={() => setShowTransactionModal(true)}
                />
              </SettingsSection>
            )}

            {/* Help & Feedback */}
            <SettingsSection title="Help & Feedback">
              <SettingsRow
                title="What's New"
                icon={Sparkles}
                onClick={() => navigate("/feedback?tab=updates")}
                rightElement={
                  hasUnreadUpdates ? (
                    <span className="w-2 h-2 rounded-full bg-brand-accent" />
                  ) : undefined
                }
              />
              <SettingsRow
                title="Feedback"
                subtitle="Suggest ideas, vote, report a bug"
                icon={Lightbulb}
                onClick={() => navigate("/feedback")}
              />
              <SettingsRow
                title="Contact"
                subtitle="Email the developer"
                icon={LifeBuoy}
                onClick={() => setShowContactSheet(true)}
              />
            </SettingsSection>

            {/* Sign Out */}
            {!isGuestOrAnonymous && (
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 py-3.5 text-brand-accent font-semibold text-sm"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            )}


            {/* Legal: quiet links, not a row each */}
            <nav aria-label="Legal" className="mt-6 mb-2 flex items-center justify-center gap-3 text-xs text-brand-textSecondary">
              <button type="button" onClick={() => navigate("/terms")} className="hover:text-brand-text underline-offset-2 hover:underline">
                Terms
              </button>
              <span aria-hidden="true">·</span>
              <button type="button" onClick={() => navigate("/refund-policy")} className="hover:text-brand-text underline-offset-2 hover:underline">
                Refunds
              </button>
              <span aria-hidden="true">·</span>
              <button type="button" onClick={() => navigate("/privacy-policy")} className="hover:text-brand-text underline-offset-2 hover:underline">
                Privacy
              </button>
            </nav>
          </>
        )}
      </div>

      {/* Coin Store Modal */}
      <CoinStore
        isOpen={showCoinStore}
        onClose={() => {
          setShowCoinStore(false);
          setCoinStorePreset(null);
        }}
        preset={coinStorePreset}
      />

      {/* Account Upgrade Modal */}
      <AccountUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onSuccess={handleUpgradeSuccess}
        onSwitchToSignIn={() => {
          setShowUpgradeModal(false);
          setShowLoginModal(true);
        }}
      />

      <AccountSheet
        isOpen={showAccountSheet}
        onClose={() => setShowAccountSheet(false)}
        accountInfo={{ ...accountInfo, displayName: displayName || accountInfo.displayName }}
        receiptEmail={savedEmail}
        onEditName={() => setShowEditName(true)}
        onEditReceiptEmail={() => setIsEditingEmail(true)}
        onPassword={() =>
          accountInfo.type === "Google Account" ? setShowLinkEmailModal(true) : setShowChangePasswordModal(true)
        }
        onDeleteAccount={() => navigate("/delete-account")}
      />

      <ProSheet
        isOpen={showProSheet}
        onClose={() => setShowProSheet(false)}
        proType={proType}
        proExpiry={proExpiry}
        cancelling={cancellingPro}
        onCancel={handleCancelSubscription}
      />

      <ContactSheet isOpen={showContactSheet} onClose={() => setShowContactSheet(false)} />

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={handleLoginSuccess}
        onSwitchToSignUp={() => {
          setShowLoginModal(false);
          setShowUpgradeModal(true);
        }}
      />

      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
      />

      <TransactionHistoryModal
        isOpen={showTransactionModal}
        onClose={() => setShowTransactionModal(false)}
        billingHistory={billingHistory}
        spendingHistory={spendingHistory}
      />


      <EditNameModal isOpen={showEditName} onClose={() => setShowEditName(false)} />
      {confirmDialog}

      <ReceiptEmailSheet
        isOpen={isEditingEmail}
        onClose={() => setIsEditingEmail(false)}
        savedEmail={savedEmail}
        onSaved={setSavedEmail}
      />

      <AddPasswordSheet
        isOpen={showLinkEmailModal}
        onClose={() => setShowLinkEmailModal(false)}
        onLinked={() => {
          setSuccessMessage("Password added. You can now sign in with your email too.");
          refreshAccountData();
        }}
      />

      <ChangePasswordSheet
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        onChanged={() => setSuccessMessage("Password changed.")}
      />
    </>
  );
};

export default Account;
